import { Controller, useForm, useWatch } from "react-hook-form";
import TextInput from "../../../../../ui/TextInput/TextInput";
import Textarea from "../../../../../ui/Textarea/Textarea";
import DetailSection from "../components/DetailSection";
import styles from "./ArticleContent.module.scss";
import { z } from "zod";
import TagSelect from "../../../../../components/TagSelect/TagSelect";
import HealthIndicator from "../../../../../components/HealthIndicator/HealthIndicator";
import { useLayoutEffect, useRef } from "react";
import Tooltip from "../../../../../ui/Tooltip/Tooltip";
import { ArticleStatus } from "../../../../../__generated__/gql/graphql";
import schema from "./schema";

type ArticleContentFormData = z.infer<typeof schema>;

function ArticleContent({
  control,
  isOpen,
  setIsOpen,
  footer,
  title,
  excerpt,
  status,
  processing,
}: {
  control: ReturnType<typeof useForm<ArticleContentFormData>>["control"];
  isOpen: boolean;
  setIsOpen: (value: string) => void;
  footer: HTMLDivElement | null;
  title: string;
  excerpt: string;
  status: ArticleStatus;
  processing: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [metaDescription] = useWatch({
    control,
    name: ["metaDescription"],
  });

  useLayoutEffect(() => {
    if (footer && ref.current && document.activeElement) {
      if (
        document.activeElement.tagName !== "BODY" &&
        document.activeElement.getBoundingClientRect().bottom >
          footer.getBoundingClientRect().top
      ) {
        ref.current.scrollBy(0, footer.offsetHeight);
      }
    }
  }, [footer]);

  return (
    <>
      <DetailSection
        ref={ref}
        title="Article Content"
        isOpen={isOpen}
        setIsOpen={() => setIsOpen("Article Content")}
        details={[
          {
            name: (
              <div className={styles.flex}>
                <span>Title</span>
                {title && <HealthIndicator max={60} value={title.length} />}
              </div>
            ),
            direction: "column",
            children:
              status !== ArticleStatus.Draft ? (
                <Tooltip content="This property can be edited within the article content">
                  <TextInput
                    disabled
                    value={title}
                    size="small"
                    className={styles.input}
                  />
                </Tooltip>
              ) : (
                <Controller
                  control={control}
                  name="title"
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      size="small"
                      className={styles.input}
                      disabled={processing}
                    />
                  )}
                />
              ),
          },
          {
            name: "Keyword",
            direction: "column",
            children: (
              <Controller
                control={control}
                name="keyword"
                render={({ field }) => (
                  <TextInput
                    {...field}
                    size="small"
                    className={styles.input}
                    disabled={processing}
                  />
                )}
              />
            ),
          },
          {
            name: "Tag",
            direction: "column",
            children: (
              <Controller
                control={control}
                name="tag"
                render={({ field }) => (
                  <TagSelect {...field} size="small" className={styles.input} />
                )}
              />
            ),
          },
          {
            name: (
              <div className={styles.flex}>
                <span>Meta description</span>
                {metaDescription && (
                  <HealthIndicator
                    max={155}
                    min={120}
                    value={metaDescription.length}
                  />
                )}
              </div>
            ),
            direction: "column",
            children: (
              <Controller
                control={control}
                name="metaDescription"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    autosize
                    minRows={3}
                    maxRows={5}
                    size="small"
                    disabled={processing}
                    className={styles.input}
                  />
                )}
              />
            ),
          },
          {
            name: (
              <div className={styles.flex}>
                <span>Excerpt</span>
                {excerpt && (
                  <HealthIndicator min={150} max={190} value={excerpt.length} />
                )}
              </div>
            ),
            direction: "column",
            children: (
              <Tooltip content="This property can be edited within the article content">
                <Textarea
                  value={excerpt}
                  disabled
                  autosize
                  minRows={3}
                  maxRows={5}
                  size="small"
                  className={styles.input}
                />
              </Tooltip>
            ),
          },
          {
            name: "Custom prompt",
            direction: "column",
            children: (
              <Controller
                control={control}
                name="additionalPrompt"
                render={({ field }) => (
                  <Textarea
                    {...field}
                    autosize
                    minRows={3}
                    maxRows={5}
                    size="small"
                    className={styles.input}
                    disabled={processing}
                    allowFullScreen
                    fullScreenLabel="Custom prompt"
                  />
                )}
              />
            ),
          },
        ]}
      />
    </>
  );
}

export default ArticleContent;
